import React from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface ImageItem {
  url: string;
  thumbnail?: string;
  title?: string;
  source?: string;
  width?: number;
  height?: number;
}

interface ImageGalleryModalProps {
  isOpen: boolean;
  images: ImageItem[];
  initialIndex: number;
  onClose: () => void;
}

export function ImageGalleryModal({
  isOpen,
  images,
  initialIndex,
  onClose,
}: ImageGalleryModalProps) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [imageLoading, setImageLoading] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);

  React.useEffect(() => {
    setCurrentIndex(initialIndex);
    setImageLoading(true);
    setImageError(false);
  }, [initialIndex, isOpen]);
  
  // Reset loading state when image changes
  React.useEffect(() => {
    setImageLoading(true);
    setImageError(false);
  }, [currentIndex]);

  // Handle keyboard navigation
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, images.length, onClose]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // Get the best image URL - prefer full URL over thumbnail
  const getImageUrl = React.useCallback((img: ImageItem): string => {
    // Prioritize url if it exists and is not empty
    if (img.url && img.url.trim() !== '') {
      return img.url;
    }
    // Fallback to thumbnail
    if (img.thumbnail && img.thumbnail.trim() !== '') {
      return img.thumbnail;
    }
    return '';
  }, []);

  // Get current image and URL (safe even if images array is empty)
  const currentImage = images[currentIndex] || null;
  const mainImageUrl = currentImage ? getImageUrl(currentImage) : '';
  
  // Debug logging
  React.useEffect(() => {
    if (isOpen && currentImage) {
      console.log('[ImageGalleryModal] Current image:', {
        index: currentIndex,
        url: currentImage.url,
        thumbnail: currentImage.thumbnail,
        mainImageUrl,
        title: currentImage.title,
      });
    }
  }, [isOpen, currentIndex, currentImage, mainImageUrl]);

  // Early return AFTER all hooks
  if (!isOpen || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 text-white">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate">
              {currentImage.title || `Hình ảnh ${currentIndex + 1} / ${images.length}`}
            </h3>
            {currentImage.source && (
              <p className="text-sm text-gray-300 mt-1 truncate">
                {currentImage.source}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 text-white hover:bg-white/10 rounded transition-colors"
            aria-label="Đóng"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Main Image */}
        <div className="flex-1 flex items-center justify-center relative min-h-0">
          {/* Previous Button */}
          {images.length > 1 && (
            <button
              onClick={handlePrevious}
              className="absolute left-4 z-10 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all"
              aria-label="Hình trước"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Image Container */}
          <div className="flex-1 flex items-center justify-center max-w-full max-h-full p-4 relative">
            {mainImageUrl ? (
              <>
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-white text-lg">Đang tải hình ảnh...</div>
                  </div>
                )}
                {imageError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
                    <p className="text-lg mb-2">Không thể tải hình ảnh</p>
                    <p className="text-sm text-gray-400 mb-4">URL: {mainImageUrl.substring(0, 100)}...</p>
                    <button
                      onClick={() => {
                        setImageError(false);
                        setImageLoading(true);
                        // Force reload by changing key
                        const img = document.querySelector(`img[data-image-key="img-${currentIndex}"]`) as HTMLImageElement;
                        if (img) {
                          const newSrc = img.src + (img.src.includes('?') ? '&' : '?') + 'retry=' + Date.now();
                          img.src = newSrc;
                        }
                      }}
                      className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded transition-colors"
                    >
                      Thử lại
                    </button>
                  </div>
                )}
                <img
                  key={`img-${currentIndex}-${mainImageUrl}`}
                  data-image-key={`img-${currentIndex}`}
                  src={mainImageUrl}
                  alt={currentImage.title || `Image ${currentIndex + 1}`}
                  className={`max-w-full max-h-full object-contain rounded-lg transition-opacity ${
                    imageLoading ? 'opacity-0' : 'opacity-100'
                  }`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    console.error('[ImageGalleryModal] Failed to load image:', mainImageUrl);
                    setImageLoading(false);
                    setImageError(true);
                  }}
                  onLoad={() => {
                    console.log('[ImageGalleryModal] Image loaded successfully:', mainImageUrl);
                    setImageLoading(false);
                    setImageError(false);
                  }}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-white p-8">
                <p className="text-lg mb-2">Không có URL hình ảnh</p>
                <p className="text-sm text-gray-400">URL: {currentImage.url || 'N/A'}</p>
                <p className="text-sm text-gray-400">Thumbnail: {currentImage.thumbnail || 'N/A'}</p>
              </div>
            )}
          </div>

          {/* Next Button */}
          {images.length > 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 z-10 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all"
              aria-label="Hình sau"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="p-4 bg-black/50">
            <div className="flex gap-2 overflow-x-auto justify-center">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition-all ${
                    index === currentIndex
                      ? 'border-white scale-110'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.thumbnail || img.url || ''}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
            <div className="text-center text-white text-sm mt-2">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

